<template>
  <div class="widget-container">
    <!-- Text Content with Markdown -->
    <div v-if="text && !compositionMode" class="widget-text">
      <div v-if="!expanded && shouldTruncate && !bypassTruncation" class="widget-text-truncated">
        <vue-markdown :content="truncatedText"
          :options="{ markdownIt: { linkify: true, inline: true, html: true } }" />
        <a href="#" @click.prevent="expanded = true" class="widget-read-more update-text">
          ...read more
        </a>
      </div>
      <div v-else>
        <vue-markdown :content="text"
          :options="{ markdownIt: { linkify: true, inline: true, html: true } }" />
      </div>
    </div>

    <!-- Composition Mode Toggle (only show if user can edit) -->
    <div v-if="editable && !compositionMode" class="widget-composition-toggle">
      <button @click="enterCompositionMode" class="p4 update-border xs-font">
        <i class="fa fa-th r4"></i> Edit Layout
      </button>
    </div>

    <!-- Composition Editor Mode -->
    <div v-if="compositionMode" class="widget-composition-editor">
      <div class="widget-composition-toolbar flex align-center between p4 b8">
        <div class="flex align-center" style="gap: 8px;">
          <button @click="addRow" class="p4 update-border xs-font">
            <i class="fa fa-plus r4"></i> Add Row
          </button>
          <button @click="exitCompositionMode" class="p4 update-border xs-font">
            <i class="fa fa-check r4"></i> Done
          </button>
        </div>
        <div class="flex align-center" style="gap: 8px;">
          <div class="flex wrap" v-for="(components, componentcategory) in $store.state.components"
            :key="'comp-' + componentcategory">
            <div class="indicate-text border-bottom xs-font">{{ componentcategory }}</div>
            <div class="flex wrap align-center">
              <span v-for="(comp, compkey) in components" :key="compkey" 
                draggable="true"
                @dragstart="onComponentDragStart($event, comp)"
                class="p4 draggable-component">
                <i v-if="'displayinstancewidgets'.indexOf(componentcategory) >= 0"
                  class="fa-sm fa fa-plus r4"></i>{{ comp }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Composition Layout -->
      <div class="widget-composition-layout">
        <div v-for="(row, rowIdx) in (compositionData && compositionData.rows ? compositionData.rows : [])" :key="'row-' + rowIdx" 
          class="widget-composition-row"
          :class="getRowClasses(row)"
          :style="getRowStyles(row)">
          <div class="widget-row-controls">
            <button @click="addColumn(rowIdx)" class="p2 xs-font" title="Add Column">
              <i class="fa fa-plus"></i>
            </button>
            <button @click="editRowStyles(rowIdx)" class="p2 xs-font" title="Edit Row Styles">
              <i class="fa fa-cog"></i>
            </button>
            <button @click="removeRow(rowIdx)" class="p2 xs-font warn-text" title="Remove Row">
              <i class="fa fa-trash"></i>
            </button>
          </div>
          
          <div class="widget-row-content widget-resizable-row" 
            :class="row.classes || ''"
            :style="getRowContentStyles(row)"
            :data-row-index="rowIdx">
            <div 
              v-for="(cell, cellIdx) in row.cells"
              :key="'cell-' + rowIdx + '-' + cellIdx"
              class="widget-composition-cell-wrapper">
              <div 
                class="widget-composition-cell"
                :class="getCellClasses(cell)"
                :style="getCellStyles(cell)"
                :data-row-index="rowIdx"
                :data-cell-index="cellIdx"
                @dragover.prevent
                @drop="onCellDrop($event, rowIdx, cellIdx)"
                @dragenter="onCellDragEnter($event)"
                @dragleave="onCellDragLeave($event)">
                
                <div class="widget-cell-controls">
                  <button @click="addColumnAt(rowIdx, cellIdx)" class="p2 xs-font" title="Add Column Before">
                    <i class="fa fa-plus"></i>
                  </button>
                  <button @click="editCellStyles(rowIdx, cellIdx)" class="p2 xs-font" title="Edit Cell Styles">
                    <i class="fa fa-cog"></i>
                  </button>
                  <button @click="removeCell(rowIdx, cellIdx)" class="p2 xs-font warn-text" title="Remove Cell">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>

                <!-- Nested Rows (for nesting) -->
                <div v-if="cell.rows && cell.rows.length > 0" class="widget-nested-rows">
                  <div v-for="(nestedRow, nestedRowIdx) in cell.rows" :key="'nested-row-' + nestedRowIdx"
                    class="widget-composition-row widget-nested-row"
                    :class="getRowClasses(nestedRow)"
                    :style="getRowStyles(nestedRow)">
                    <div class="widget-row-controls">
                      <button @click="addColumnToNested(rowIdx, cellIdx, nestedRowIdx)" class="p2 xs-font">
                        <i class="fa fa-plus"></i>
                      </button>
                      <button @click="removeNestedRow(rowIdx, cellIdx, nestedRowIdx)" class="p2 xs-font warn-text">
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                    <div class="widget-row-content flex wrap">
                      <div v-for="(nestedCell, nestedCellIdx) in nestedRow.cells"
                        :key="'nested-cell-' + nestedRowIdx + '-' + nestedCellIdx"
                        class="widget-composition-cell"
                        :class="getCellClasses(nestedCell)"
                        :style="getCellStyles(nestedCell)"
                        @dragover.prevent
                        @drop="onNestedCellDrop($event, rowIdx, cellIdx, nestedRowIdx, nestedCellIdx)"
                        @dragenter="onCellDragEnter($event)"
                        @dragleave="onCellDragLeave($event)">
                        <div class="widget-cell-dropzone">
                          <component v-for="(comp, compIdx) in nestedCell.components" 
                            :key="'comp-' + compIdx"
                            :is="comp.name" 
                            v-bind="comp.props"
                            :style="comp.styles"
                            :class="comp.classes" />
                          <div v-if="!nestedCell.components || nestedCell.components.length === 0" 
                            class="widget-empty-cell">
                            Drop component here
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Cell Content / Drop Zone -->
                <div class="widget-cell-dropzone">
                  <component v-for="(comp, compIdx) in cell.components" 
                    :key="'comp-' + compIdx"
                    :is="comp.name" 
                    v-bind="comp.props"
                    :style="comp.styles"
                    :class="comp.classes" />
                  <div v-if="!cell.components || cell.components.length === 0" 
                    class="widget-empty-cell">
                    Drop component here
                  </div>
                </div>

                <!-- Add Nested Row Button -->
                <button @click="addNestedRow(rowIdx, cellIdx)" class="widget-add-nested-row p2 xs-font">
                  <i class="fa fa-plus r4"></i> Add Nested Row
                </button>
              </div>
              
              <!-- Draggable Separator (between cells) -->
              <div 
                v-if="cellIdx < row.cells.length - 1"
                class="widget-cell-separator"
                :class="{ 'widget-separator-active': draggingSeparator && draggingSeparator.rowIdx === rowIdx && draggingSeparator.cellIdx === cellIdx }"
                @mousedown="startResize($event, rowIdx, cellIdx, 'horizontal')"
                @touchstart="startResize($event, rowIdx, cellIdx, 'horizontal')">
                <div class="widget-separator-handle"></div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!compositionData || !compositionData.rows || compositionData.rows.length === 0" class="widget-empty-composition">
          <p>No rows yet. Click "Add Row" to start building your layout.</p>
        </div>
      </div>
    </div>

    <!-- Render Composition (when not in edit mode) -->
    <div v-else-if="composition && composition.rows && Array.isArray(composition.rows) && composition.rows.length > 0" class="widget-composition-render">
      <div v-for="(row, rowIdx) in composition.rows" :key="'render-row-' + rowIdx"
        class="widget-render-row"
        :class="getRowClasses(row)"
        :style="getRowStyles(row)">
        <div class="widget-render-row-content flex wrap" 
          :class="row.classes || ''"
          :style="getRowContentStyles(row)">
          <div v-for="(cell, cellIdx) in row.cells" :key="'render-cell-' + rowIdx + '-' + cellIdx"
            class="widget-render-cell"
            :class="getCellClasses(cell)"
            :style="getCellStyles(cell)">
            
            <!-- Nested Rows -->
            <div v-if="cell.rows && cell.rows.length > 0" class="widget-render-nested">
              <div v-for="(nestedRow, nestedRowIdx) in cell.rows" 
                :key="'render-nested-row-' + nestedRowIdx"
                class="widget-render-row"
                :class="getRowClasses(nestedRow)"
                :style="getRowStyles(nestedRow)">
                <div class="widget-render-row-content flex wrap">
                  <div v-for="(nestedCell, nestedCellIdx) in nestedRow.cells"
                    :key="'render-nested-cell-' + nestedCellIdx"
                    class="widget-render-cell"
                    :class="getCellClasses(nestedCell)"
                    :style="getCellStyles(nestedCell)">
                    <component v-for="(comp, compIdx) in nestedCell.components" 
                      :key="'render-comp-' + compIdx"
                      :is="comp.name" 
                      v-bind="comp.props"
                      :style="comp.styles"
                      :class="comp.classes" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Cell Components -->
            <component v-for="(comp, compIdx) in cell.components" 
              :key="'render-comp-' + compIdx"
              :is="comp.name" 
              v-bind="comp.props"
              :style="comp.styles"
              :class="comp.classes" />
          </div>
        </div>
      </div>
    </div>

    <!-- Legacy Components Render (if no composition) -->
    <div v-else-if="components && components.length > 0" class="widget-components">
      <component v-for="(comp, idx) in components" :key="idx" :is="comp.name" v-bind="comp.props"
        :style="comp.styles" />
    </div>

    <!-- Render media -->
    <div v-if="media && media.length > 0 && !compositionMode" class="widget-media">
      <div v-for="(mediaItem, index) in media" :key="index" style="margin: 4px 0;">
        <img v-if="getMediaUrl(mediaItem, userId) && getMediaUrl(mediaItem, userId).match(/\.(png|jpe?g|gif|webp)(\?.*)?$/i)"
          :src="getMediaUrl(mediaItem, userId)"
          class="widget-media-item"
          style="max-width: 100%; height: auto; border-radius: 4px;" />
        <a v-else :href="getMediaUrl(mediaItem, userId)" target="_blank" rel="noopener noreferrer" class="widget-media-link">
          {{ getMediaUrl(mediaItem, userId) }}
        </a>
      </div>
    </div>

    <!-- Render links -->
    <div v-if="links && links.length > 0 && !compositionMode" class="widget-links">
      <div v-for="(link, index) in links" :key="index" class="widget-link">
        <a :href="link" target="_blank" rel="noopener noreferrer" class="widget-link-item">
          {{ link }}
        </a>
      </div>
    </div>

    <!-- Render embeds -->
    <div v-if="embeds && !compositionMode" class="widget-embeds">
      <div v-html="processEmbed(embeds)" ref="embedContainer"></div>
    </div>

    <!-- Style Editor Modal -->
    <div v-if="editingStyles" class="widget-style-editor-modal" @click.self="closeStyleEditor">
      <div class="widget-style-editor-content p8 b8 r4">
        <div class="flex align-center between b8">
          <h3>Edit Styles & Classes</h3>
          <button @click="closeStyleEditor" class="p4">×</button>
        </div>
        
        <div class="p4">
          <label class="xs-font">CSS Classes (space-separated)</label>
          <input v-model="editingStyles.classes" type="text" class="xs-input flexw p4" 
            placeholder="e.g. p8 b8 r4 flex" />
        </div>

        <div class="p4">
          <label class="xs-font">Inline Styles (JSON)</label>
          <textarea v-model="editingStyles.stylesText" class="xs-input flexw p4" 
            placeholder='{"padding": "8px", "background": "#f0f0f0"}' 
            rows="6"></textarea>
        </div>

        <div class="flex justify-end p4" style="gap: 8px;">
          <button @click="closeStyleEditor" class="p4">Cancel</button>
          <button @click="saveStyles" class="p4 update">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "widget",
  props: {
    text: { type: String, default: "" },
    components: { type: Array, default: () => [] },
    media: { type: Array, default: () => [] },
    links: { type: Array, default: () => [] },
    embeds: { type: String, default: "" },
    userId: { type: String, default: "" },
    truncateLength: { type: Number, default: 560 },
    bypassTruncation: { type: Boolean, default: false },
    editable: { type: Boolean, default: false },
    composition: { type: Object, default: null }
  },
  data() {
    return {
      expanded: false,
      compositionMode: false,
      editingStyles: null,
      editingTarget: null, // { type: 'row'|'cell', rowIdx, cellIdx?, nestedRowIdx?, nestedCellIdx? }
      draggedComponent: null,
      compositionData: null,
      draggingSeparator: null,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidths: [],
      isResizing: false
    };
  },
  computed: {
    shouldTruncate() {
      if (!this.text) return false;
      return this.text.length > this.truncateLength;
    },
    truncatedText() {
      if (!this.text || !this.shouldTruncate) return this.text;
      
      const truncated = this.text.slice(0, this.truncateLength);
      const lastSpace = truncated.lastIndexOf(' ');
      const lastNewline = truncated.lastIndexOf('\n');
      const lastBoundary = Math.max(lastSpace, lastNewline);
      
      if (lastBoundary > 0) {
        return truncated.slice(0, lastBoundary);
      }
      return truncated;
    }
  },
  watch: {
    composition: {
      handler(newVal) {
        if (newVal && newVal.rows) {
          this.compositionData = JSON.parse(JSON.stringify(newVal));
        } else if (!this.compositionData) {
          this.compositionData = { rows: [] };
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    enterCompositionMode() {
      if (!this.compositionData || !this.compositionData.rows) {
        this.compositionData = { rows: [] };
      }
      this.compositionMode = true;
      this.$emit('composition-mode-changed', true);
    },
    exitCompositionMode() {
      this.compositionMode = false;
      this.$emit('composition-updated', this.compositionData);
      this.$emit('composition-mode-changed', false);
    },
    addRow() {
      if (!this.compositionData) {
        this.compositionData = { rows: [] };
      }
      if (!this.compositionData.rows) {
        this.$set(this.compositionData, 'rows', []);
      }
      this.compositionData.rows.push({
        cells: [{ components: [], width: null }],
        classes: 'p8',
        styles: {}
      });
    },
    addColumn(rowIdx) {
      if (this.compositionData.rows[rowIdx]) {
        this.compositionData.rows[rowIdx].cells.push({ components: [], width: null });
        this.normalizeCellWidths(rowIdx);
      }
    },
    normalizeCellWidths(rowIdx) {
      const row = this.compositionData.rows[rowIdx];
      if (!row || !row.cells) return;
      
      const totalWidth = 100;
      const cellsWithWidth = row.cells.filter(c => c.width !== null && c.width !== undefined).length;
      const cellsWithoutWidth = row.cells.length - cellsWithWidth;
      
      if (cellsWithoutWidth > 0) {
        const autoWidth = (totalWidth - row.cells.reduce((sum, c) => sum + (c.width || 0), 0)) / cellsWithoutWidth;
        row.cells.forEach(cell => {
          if (cell.width === null || cell.width === undefined) {
            this.$set(cell, 'width', autoWidth);
          }
        });
      }
    },
    addColumnAt(rowIdx, cellIdx) {
      if (this.compositionData.rows[rowIdx]) {
        this.compositionData.rows[rowIdx].cells.splice(cellIdx, 0, { components: [], width: null });
        this.normalizeCellWidths(rowIdx);
      }
    },
    removeRow(rowIdx) {
      if (this.compositionData.rows && this.compositionData.rows[rowIdx]) {
        this.compositionData.rows.splice(rowIdx, 1);
      }
    },
    removeCell(rowIdx, cellIdx) {
      if (this.compositionData.rows[rowIdx] && this.compositionData.rows[rowIdx].cells[cellIdx]) {
        this.compositionData.rows[rowIdx].cells.splice(cellIdx, 1);
      }
    },
    addNestedRow(rowIdx, cellIdx) {
      if (!this.compositionData.rows[rowIdx].cells[cellIdx].rows) {
        this.$set(this.compositionData.rows[rowIdx].cells[cellIdx], 'rows', []);
      }
      this.compositionData.rows[rowIdx].cells[cellIdx].rows.push({
        cells: [{ components: [] }],
        classes: 'p4',
        styles: {}
      });
    },
    addColumnToNested(rowIdx, cellIdx, nestedRowIdx) {
      if (this.compositionData.rows[rowIdx].cells[cellIdx].rows[nestedRowIdx]) {
        this.compositionData.rows[rowIdx].cells[cellIdx].rows[nestedRowIdx].cells.push({ components: [] });
      }
    },
    removeNestedRow(rowIdx, cellIdx, nestedRowIdx) {
      if (this.compositionData.rows[rowIdx].cells[cellIdx].rows[nestedRowIdx]) {
        this.compositionData.rows[rowIdx].cells[cellIdx].rows.splice(nestedRowIdx, 1);
      }
    },
    onComponentDragStart(event, componentName) {
      this.draggedComponent = componentName;
      event.dataTransfer.effectAllowed = 'move';
    },
    onCellDrop(event, rowIdx, cellIdx) {
      event.preventDefault();
      if (this.draggedComponent) {
        if (!this.compositionData.rows[rowIdx].cells[cellIdx].components) {
          this.$set(this.compositionData.rows[rowIdx].cells[cellIdx], 'components', []);
        }
        this.compositionData.rows[rowIdx].cells[cellIdx].components.push({
          name: this.draggedComponent,
          props: {},
          styles: {},
          classes: ''
        });
        this.draggedComponent = null;
      }
      event.currentTarget.classList.remove('widget-drop-active');
    },
    onNestedCellDrop(event, rowIdx, cellIdx, nestedRowIdx, nestedCellIdx) {
      event.preventDefault();
      if (this.draggedComponent) {
        const nestedCell = this.compositionData.rows[rowIdx].cells[cellIdx].rows[nestedRowIdx].cells[nestedCellIdx];
        if (!nestedCell.components) {
          this.$set(nestedCell, 'components', []);
        }
        nestedCell.components.push({
          name: this.draggedComponent,
          props: {},
          styles: {},
          classes: ''
        });
        this.draggedComponent = null;
      }
      event.currentTarget.classList.remove('widget-drop-active');
    },
    onCellDragEnter(event) {
      event.currentTarget.classList.add('widget-drop-active');
    },
    onCellDragLeave(event) {
      event.currentTarget.classList.remove('widget-drop-active');
    },
    editRowStyles(rowIdx) {
      const row = this.compositionData.rows[rowIdx];
      this.editingTarget = { type: 'row', rowIdx };
      this.editingStyles = {
        classes: row.classes || '',
        stylesText: JSON.stringify(row.styles || {}, null, 2)
      };
    },
    editCellStyles(rowIdx, cellIdx, nestedRowIdx = null, nestedCellIdx = null) {
      let cell;
      if (nestedRowIdx !== null && nestedCellIdx !== null) {
        cell = this.compositionData.rows[rowIdx].cells[cellIdx].rows[nestedRowIdx].cells[nestedCellIdx];
        this.editingTarget = { type: 'cell', rowIdx, cellIdx, nestedRowIdx, nestedCellIdx };
      } else {
        cell = this.compositionData.rows[rowIdx].cells[cellIdx];
        this.editingTarget = { type: 'cell', rowIdx, cellIdx };
      }
      this.editingStyles = {
        classes: cell.classes || '',
        stylesText: JSON.stringify(cell.styles || {}, null, 2)
      };
    },
    closeStyleEditor() {
      this.editingStyles = null;
      this.editingTarget = null;
    },
    saveStyles() {
      if (!this.editingTarget || !this.editingStyles) return;
      
      try {
        const styles = JSON.parse(this.editingStyles.stylesText || '{}');
        
        if (this.editingTarget.type === 'row') {
          const row = this.compositionData.rows[this.editingTarget.rowIdx];
          row.classes = this.editingStyles.classes;
          row.styles = styles;
        } else if (this.editingTarget.type === 'cell') {
          let cell;
          if (this.editingTarget.nestedRowIdx !== null && this.editingTarget.nestedCellIdx !== null) {
            cell = this.compositionData.rows[this.editingTarget.rowIdx]
              .cells[this.editingTarget.cellIdx]
              .rows[this.editingTarget.nestedRowIdx]
              .cells[this.editingTarget.nestedCellIdx];
          } else {
            cell = this.compositionData.rows[this.editingTarget.rowIdx].cells[this.editingTarget.cellIdx];
          }
          cell.classes = this.editingStyles.classes;
          cell.styles = styles;
        }
        
        this.closeStyleEditor();
      } catch (e) {
        console.error('Invalid JSON for styles:', e);
        alert('Invalid JSON format for styles');
      }
    },
    getRowClasses(row) {
      return row.classes || 'p8';
    },
    getRowStyles(row) {
      return row.styles || {};
    },
    getRowContentStyles(row) {
      return {
        gap: '8px',
        ...(row.contentStyles || {})
      };
    },
    getCellClasses(cell) {
      return cell.classes || 'p4 b8 r4';
    },
    getCellStyles(cell) {
      const baseStyles = {
        minHeight: '60px',
        ...(cell.styles || {})
      };
      
      // If cell has explicit width, use it
      if (cell.width !== null && cell.width !== undefined) {
        baseStyles.width = `${cell.width}%`;
        baseStyles.flex = '0 0 auto';
      } else {
        baseStyles.flex = '1 1 auto';
      }
      
      return baseStyles;
    },
    startResize(event, rowIdx, cellIdx, direction) {
      event.preventDefault();
      event.stopPropagation();
      
      this.isResizing = true;
      this.draggingSeparator = { rowIdx, cellIdx, direction };
      
      const row = this.compositionData.rows[rowIdx];
      if (!row || !row.cells) return;
      
      // Store initial positions and widths
      this.resizeStartX = event.clientX || (event.touches && event.touches[0].clientX) || 0;
      this.resizeStartY = event.clientY || (event.touches && event.touches[0].clientY) || 0;
      
      // Store current widths
      this.resizeStartWidths = row.cells.map(cell => cell.width || null);
      
      // Ensure cells have widths
      row.cells.forEach((cell) => {
        if (cell.width === null || cell.width === undefined) {
          this.$set(cell, 'width', 100 / row.cells.length);
        }
      });
      
      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
      document.addEventListener('touchmove', this.handleResize);
      document.addEventListener('touchend', this.stopResize);
      
      document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    },
    handleResize(event) {
      if (!this.isResizing || !this.draggingSeparator) return;
      
      event.preventDefault();
      
      const row = this.compositionData.rows[this.draggingSeparator.rowIdx];
      if (!row || !row.cells) return;
      
      const currentX = event.clientX || (event.touches && event.touches[0].clientX) || 0;
      
      if (this.draggingSeparator.direction === 'horizontal') {
        const rowElement = document.querySelector(`[data-row-index="${this.draggingSeparator.rowIdx}"].widget-resizable-row`);
        if (!rowElement) return;
        
        const rowWidth = rowElement.offsetWidth;
        const deltaX = currentX - this.resizeStartX;
        const deltaPercent = (deltaX / rowWidth) * 100;
        
        const leftCell = row.cells[this.draggingSeparator.cellIdx];
        const rightCell = row.cells[this.draggingSeparator.cellIdx + 1];
        
        if (leftCell && rightCell) {
          const leftWidth = this.resizeStartWidths[this.draggingSeparator.cellIdx] || (100 / row.cells.length);
          const rightWidth = this.resizeStartWidths[this.draggingSeparator.cellIdx + 1] || (100 / row.cells.length);
          
          const newLeftWidth = Math.max(10, Math.min(90, leftWidth + deltaPercent));
          const newRightWidth = Math.max(10, Math.min(90, rightWidth - deltaPercent));
          
          this.$set(leftCell, 'width', newLeftWidth);
          this.$set(rightCell, 'width', newRightWidth);
        }
      }
    },
    stopResize() {
      this.isResizing = false;
      this.draggingSeparator = null;
      
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.handleResize);
      document.removeEventListener('touchend', this.stopResize);
      
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    },
    getMediaUrl(mediaItem, userId) {
      if (!mediaItem) return '';
      
      if (typeof mediaItem === 'string' && (mediaItem.startsWith('http://') || mediaItem.startsWith('https://') || mediaItem.startsWith('data:'))) {
        // Heuristic: if this URL points to a comment/page URL like /page/<id>, try to resolve <id> to a stored file URL
        try {
          const parsed = new URL(mediaItem);
          const pageMatch = parsed.pathname.match(/\/page\/(\w+)/);
          if (pageMatch && pageMatch[1]) {
            const possibleId = pageMatch[1];
            const userFiles = this.$store.state.user && this.$store.state.user[userId] && this.$store.state.user[userId].files;
            if (userFiles && userFiles[possibleId] && userFiles[possibleId].url) {
              return typeof userFiles[possibleId].url === 'string' ? userFiles[possibleId].url : (userFiles[possibleId].url[0] || mediaItem);
            }
            const usersFiles = this.$store.state.users && this.$store.state.users[userId] && this.$store.state.users[userId].files;
            if (usersFiles && usersFiles[possibleId] && usersFiles[possibleId].url) {
              return typeof usersFiles[possibleId].url === 'string' ? usersFiles[possibleId].url : (usersFiles[possibleId].url[0] || mediaItem);
            }
          }
        } catch (e) {
          // Silently ignore URL parsing errors and use original mediaItem
        }
        return mediaItem;
      }
      
      const userFiles = this.$store.state.user && this.$store.state.user[userId] && this.$store.state.user[userId].files;
      if (userFiles && userFiles[mediaItem]) {
        const file = userFiles[mediaItem];
        if (file.url) {
          return typeof file.url === 'string' ? file.url : (file.url[0] || '');
        }
      }
      
      const usersFiles = this.$store.state.users && this.$store.state.users[userId] && this.$store.state.users[userId].files;
      if (usersFiles && usersFiles[mediaItem]) {
        const file = usersFiles[mediaItem];
        if (file.url) {
          return typeof file.url === 'string' ? file.url : (file.url[0] || '');
        }
      }
      
      return mediaItem;
    },
    processEmbed(embedCode) {
      if (!embedCode) return '';
      return embedCode;
    }
  }
};
</script>

<style scoped>
.widget-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
}

.widget-text {
  margin-bottom: 8px;
}

.widget-text-truncated {
  display: inline;
}

.widget-read-more {
  margin-left: 4px;
  text-decoration: none;
  cursor: pointer;
}

.widget-read-more:hover {
  text-decoration: underline;
}

.widget-composition-toggle {
  margin: 8px 0;
}

.widget-composition-editor {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 8px;
  background: #fafafa;
}

.widget-composition-toolbar {
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 8px;
}

.draggable-component {
  cursor: grab;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  margin: 4px;
}

.draggable-component:hover {
  background: #f0f0f0;
}

.draggable-component:active {
  cursor: grabbing;
}

.widget-composition-layout {
  min-height: 200px;
}

.widget-composition-row {
  position: relative;
  margin: 8px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  padding: 8px;
}

.widget-nested-row {
  margin: 4px 0;
  border-color: #bbb;
}

.widget-row-controls {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
  z-index: 10;
}

.widget-row-controls button {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 2px;
  cursor: pointer;
}

.widget-row-content {
  margin-top: 24px;
  position: relative;
}

.widget-resizable-row {
  display: flex;
  position: relative;
}

.widget-composition-cell-wrapper {
  display: flex;
  align-items: stretch;
  position: relative;
}

.widget-composition-cell {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 4px;
  min-height: 60px;
  padding: 8px;
  margin: 4px;
  background: #fafafa;
  transition: all 0.2s;
}

.widget-composition-cell.widget-drop-active {
  border-color: #007bff;
  background: #e7f3ff;
  border-style: solid;
}

.widget-cell-controls {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
  z-index: 10;
}

.widget-cell-controls button {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10px;
  padding: 2px 4px;
}

.widget-cell-dropzone {
  min-height: 40px;
  padding: 8px;
}

.widget-empty-cell {
  text-align: center;
  color: #999;
  padding: 20px;
  font-style: italic;
  border: 1px dashed #ddd;
  border-radius: 4px;
}

.widget-add-nested-row {
  margin-top: 8px;
  width: 100%;
  border: 1px dashed #ccc;
  background: #f0f0f0;
  cursor: pointer;
}

.widget-empty-composition {
  text-align: center;
  padding: 40px;
  color: #999;
}

.widget-composition-render {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget-render-row {
  display: flex;
  flex-direction: column;
}

.widget-render-row-content {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.widget-render-cell {
  flex: 1 1 auto;
  min-width: 0;
}

.widget-render-nested {
  width: 100%;
}

.widget-components {
  margin: 8px 0;
}

.widget-media {
  margin: 8px 0;
}

.widget-media-item {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 4px 0;
}

.widget-links {
  margin: 8px 0;
}

.widget-link {
  margin: 4px 0;
}

.widget-link-item {
  color: #007bff;
  text-decoration: none;
}

.widget-link-item:hover {
  text-decoration: underline;
}

.widget-embeds {
  margin: 8px 0;
}

.widget-style-editor-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.widget-style-editor-content {
  background: white;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.widget-cell-separator {
  position: relative;
  width: 4px;
  min-width: 4px;
  background: transparent;
  cursor: col-resize;
  z-index: 20;
  margin: 0 -2px;
  transition: background 0.2s;
}

.widget-cell-separator:hover,
.widget-cell-separator.widget-separator-active {
  background: #007bff;
}

.widget-separator-handle {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 100%;
  background: #ccc;
  border-radius: 2px;
  transition: background 0.2s, width 0.2s;
}

.widget-cell-separator:hover .widget-separator-handle,
.widget-cell-separator.widget-separator-active .widget-separator-handle {
  background: #007bff;
  width: 6px;
}
</style>
